import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  FormatBold as Bold,
  FormatItalic as Italic,
  FormatUnderlined as Underline,
  FormatAlignLeft as AlignLeft,
  FormatAlignCenter as AlignCenter,
  FormatAlignRight as AlignRight,
  FormatListBulleted as List,
  FormatListNumbered as ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  TableChart as Table,
  Undo,
  Redo,
  Code,
  FormatQuote as Quote,
  LooksOne as Heading1,
  LooksTwo as Heading2,
  StrikethroughS as Strikethrough,
  Close as X,
  Link,
} from "@mui/icons-material";
import "../../../src/styles.css";

interface MentionUser {
  id: string;
  name: string;
  identifier?: string;
  stage_name?: string;
  stage_id?: string;
}

interface ColumnNameData {
  key: string;
  value: string;
  added_at?: string;
}

interface StageConfigData {
  id: string;
  name: string;
  identifier: string;
  stage_name?: string;
  stage_id?: string;
  stage_configs?: any[];
}

interface PreviousStageData {
  id: number;
  name: string;
  stage_configs: StageConfigData[];
}

interface CustomRichTextEditorProps {
  type: string;
  column_names?: ColumnNameData[];
  previous_stages?: PreviousStageData[];
  onContentChange?: (html: string) => void;
  initialContent?: string;
}

const CustomRichTextEditor: React.FC<CustomRichTextEditorProps> = ({ type,
  column_names = [],
  previous_stages = [],
  onContentChange,
  initialContent = '' }) => {
  const [content, setContent] = useState<string>(initialContent);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [tableRows, setTableRows] = useState("3");
  const [tableCols, setTableCols] = useState("3");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<MentionUser[]>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const mentionStartPosRef = useRef<{ node: Node; offset: number } | null>(
    null
  );
  const savedSelectionRef = useRef<Range | null>(null);
  const [plainTextMode, setPlainTextMode] = useState(false);
  const htmlContentBeforePlainText = useRef<string>("");




  useEffect(() => {
    if (editorRef.current) {
      if (initialContent) {
        const parsedContent = parseInitialContent(initialContent);
        editorRef.current.innerHTML = parsedContent;
        editorRef.current.dataset.placeholder = "";
      } else {
        editorRef.current.innerHTML = "";
        editorRef.current.dataset.placeholder =
          "Start typing here... Type @ to insert fields";
      }
    }

    //setDropdownValues();

    // Enable default paragraph creation
    document.execCommand("defaultParagraphSeparator", false, "p");
  }, [initialContent]);

  useEffect(() => {
    setDropdownValues();
  }, [type, column_names, previous_stages]);

  const setDropdownValues = async () => {
    let mentionData: MentionUser[] = [];

    if (type === "DYNAMIC" && column_names.length > 0) {
      mentionData = column_names.map((col) => ({
        id: col.key,
        name: col.value,
        identifier: col.key,
      }));
    } else if (type === "PREVIOUS_STAGE" && previous_stages.length > 0) {
      mentionData = previous_stages.flatMap((stage) =>
        stage.stage_configs.map((config) => ({
          id: config.id,
          name: stage.name,
          identifier: config.identifier,
          stage_name: stage.name,
          stage_id: stage.id?.toString(),
        }))
      );
    }

    setUsers(mentionData);
  };

  const execCommand = (
    command: string,
    value: string | undefined = undefined
  ) => {
    if (!editorRef.current) return;

    editorRef.current.focus();

    // Clear placeholder before executing command
    if (editorRef.current.dataset.placeholder) {
      editorRef.current.dataset.placeholder = "";
    }

    document.execCommand(command, false, value);

    setTimeout(() => {
      if (editorRef.current) {
        updateContent();
      }
    }, 50);
  };

  const updateContent = () => {
    if (editorRef.current) {
      const clonedContent = editorRef.current.cloneNode(true) as HTMLElement;
      const mentionChips = clonedContent.querySelectorAll(
        '[data-type="mention"]'
      );

      mentionChips.forEach((chip) => {
        const name = chip.getAttribute("data-mention-name");
        const id = chip.getAttribute("data-mention-id");
        if (id) {
          const textNode = document.createTextNode(`{${id}}`);
          chip.parentNode?.replaceChild(textNode, chip);
        }
      });

      setContent(clonedContent.innerHTML);
      if (onContentChange)
        onContentChange(clonedContent.innerHTML);
      let output = clonedContent.innerHTML;

      if (plainTextMode) {
        // Strip ALL HTML tags
        output = clonedContent.innerText || clonedContent.textContent || "";
      } else {
        // Send HTML
        output = clonedContent.innerHTML || "";
      }

      setContent(output);
      if (onContentChange) onContentChange(output);
    }
  };

  // Add this function - it parses HTML and converts {mentions} to chips
  const parseInitialContent = (html: string): string => {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all text nodes and replace {mention} patterns with chips
    const walkTextNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        // const text = node.textContent;
        const text = node.textContent.replace(/\u00A0/g, " ");
        // Match {anything} pattern
        const mentionRegex = /\{([^}]+)\}/g;

        if (mentionRegex.test(text)) {
          // Create a document fragment to hold the new nodes
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;

          // Reset regexconst text = node.textContent;
          mentionRegex.lastIndex = 0;
          let match;

          while ((match = mentionRegex.exec(text)) !== null) {
            // Add text before the mention
            if (match.index > lastIndex) {
              fragment.appendChild(
                document.createTextNode(text.substring(lastIndex, match.index))
              );
            }

            // Create the mention chip
            const mentionValue = match[1]; // e.g., "task_name" or "checkbox.Checkbox workflow 1"
            const chip = createMentionChip(mentionValue);
            fragment.appendChild(chip);

            // Add space after chip
            fragment.appendChild(document.createTextNode('\u00A0'));

            lastIndex = match.index + match[0].length;
          }

          // Add remaining text
          if (lastIndex < text.length) {
            fragment.appendChild(
              document.createTextNode(text.substring(lastIndex))
            );
          }

          // Replace the text node with the fragment
          node.parentNode?.replaceChild(fragment, node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Recursively process child nodes
        const children = Array.from(node.childNodes);
        children.forEach(child => walkTextNodes(child));
      }
    };

    walkTextNodes(tempDiv);
    return tempDiv.innerHTML;
  };

  const stageConfigMap = useMemo(() => {
    const map = new Map<string, { identifier: string; stageName: string }>();
    previous_stages.forEach(stage => {
      stage.stage_configs.forEach(config => {
        map.set(config.id, {
          identifier: config.identifier,
          stageName: stage.name,
        });
      });
    });
    return map;
  }, [previous_stages]);


  const createMentionChip = (mentionId: string): HTMLSpanElement => {
    const config = stageConfigMap.get(mentionId);

    if (!config) {
      const fallback = document.createElement("span");
      fallback.textContent = `{${mentionId}}`;
      fallback.style.color = "red";
      return fallback;
    }

    const displayText = `${config.identifier} - ${config.stageName}`;

    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.className = "mention-chip";
    chip.style.display = "inline-block";
    chip.style.backgroundColor = "#e0e7ff";
    chip.style.color = "#3730a3";
    chip.style.padding = "2px 8px";
    chip.style.borderRadius = "12px";
    chip.style.fontSize = "14px";
    chip.style.margin = "0 2px";
    chip.style.cursor = "default";

    chip.setAttribute("data-type", "mention");
    chip.setAttribute("data-mention-id", mentionId);
    chip.setAttribute("data-mention-name", mentionId);

    chip.textContent = displayText;

    return chip;
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (savedSelectionRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  };

  const insertTable = () => {
    if (editorRef.current) {
      const numRows = parseInt(tableRows);
      const numCols = parseInt(tableCols);

      if (numRows > 0 && numCols > 0) {
        editorRef.current.focus();

        const table = document.createElement("table");
        table.setAttribute("border", "1");
        table.style.borderCollapse = "collapse";
        table.style.width = "100%";
        table.style.margin = "10px 0";

        const tbody = document.createElement("tbody");

        for (let i = 0; i < numRows; i++) {
          const tr = document.createElement("tr");
          for (let j = 0; j < numCols; j++) {
            const td = document.createElement("td");
            td.style.border = "1px solid #ddd";
            td.style.padding = "8px";
            td.style.minWidth = "50px";
            td.innerHTML = "<br>";
            tr.appendChild(td);
          }
          tbody.appendChild(tr);
        }

        table.appendChild(tbody);
        editorRef.current.appendChild(table);

        const p = document.createElement("p");
        p.innerHTML = "<br>";
        editorRef.current.appendChild(p);

        updateContent();
        setShowTableModal(false);
        setTableRows("3");
        setTableCols("3");
      }
    }
  };

  const insertLink = () => {
    if (linkUrl && editorRef.current) {
      restoreSelection();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();

        const link = document.createElement("a");
        link.href = linkUrl;
        link.textContent = selectedText || linkUrl;
        link.style.color = "#2563eb";
        link.style.textDecoration = "underline";
        link.target = "_blank";

        range.deleteContents();
        range.insertNode(link);

        const space = document.createTextNode("\u00A0");
        link.parentNode?.insertBefore(space, link.nextSibling);

        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      setShowLinkModal(false);
      setLinkUrl("");
      updateContent();
      editorRef.current.focus();
    }
  };

  const insertImage = () => {
    if (imageUrl && editorRef.current) {
      restoreSelection();

      const img = document.createElement("img");
      img.src = imageUrl;
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.margin = "10px 0";
      img.style.display = "block";

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(img);

        const p = document.createElement("p");
        p.innerHTML = "<br>";
        img.parentNode?.insertBefore(p, img.nextSibling);

        range.setStart(p, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      setShowImageModal(false);
      setImageUrl("");
      updateContent();
      editorRef.current.focus();
    }
  };

  const insertMention = (user: MentionUser) => {
    if (!editorRef.current || !mentionStartPosRef.current) return;

    const { node, offset } = mentionStartPosRef.current;

    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const text = node.textContent;
      const beforeAt = text.substring(0, offset);
      const afterSearch = text.substring(offset + mentionSearch.length + 1);

      // Create text nodes
      const beforeNode = document.createTextNode(beforeAt);

      let displayText = '';
      let mentionValue = '';

      if (type === 'DYNAMIC') {
        displayText = user.name;
        mentionValue = user.identifier!;
      } else if (type === "PREVIOUS_STAGE") {
        displayText = `${user.identifier} - ${user.name}`;
        mentionValue = `${user.identifier}.${user.stage_id}`
      }

      
      const chip = document.createElement("span");
      chip.contentEditable = "false";
      chip.className = "mention-chip";
      chip.style.display = "inline-block";
      chip.style.backgroundColor = "#e0e7ff";
      chip.style.color = "#3730a3";
      chip.style.padding = "2px 8px";
      chip.style.borderRadius = "12px";
      chip.style.fontSize = "14px";
      chip.style.margin = "0 2px";
      chip.style.cursor = "default";

      chip.setAttribute("data-mention-id", user.id);
      chip.setAttribute("data-mention-name", user.id);
      chip.setAttribute("data-mention-display", displayText);
      chip.setAttribute("data-type", "mention");
      chip.textContent = displayText;

      const afterNode = document.createTextNode("\u00A0" + afterSearch);

      const parent = node.parentNode;
      if (parent) {
        parent.insertBefore(beforeNode, node);
        parent.insertBefore(chip, node);
        parent.insertBefore(afterNode, node);
        parent.removeChild(node);

        // Set cursor after the chip
        const range = document.createRange();
        range.setStart(afterNode, 1);
        range.collapse(true);

        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }

    setShowMentions(false);
    setMentionSearch("");
    mentionStartPosRef.current = null;

    setTimeout(() => {
      updateContent();
      editorRef.current?.focus();
    }, 0);
  };

  const handleInput = () => {
    // Ensure there's always at least a paragraph
    if (editorRef.current) {
      if (
        !editorRef.current.innerHTML ||
        editorRef.current.innerHTML === "<br>" ||
        editorRef.current.innerHTML.trim() === ""
      ) {
        editorRef.current.innerHTML = "<p><br></p>";

        // Set cursor inside the paragraph
        const range = document.createRange();
        const selection = window.getSelection();
        const p = editorRef.current.querySelector("p");
        if (p && selection) {
          range.setStart(p, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }

    updateContent();

    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const text = node.textContent;
      const cursorPos = range.startOffset;
      const textBeforeCursor = text.substring(0, cursorPos);

      const lastAtIndex = textBeforeCursor.lastIndexOf("@");

      if (lastAtIndex !== -1) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);

        if (!searchText.includes(" ") && !searchText.includes("\n")) {
          setMentionSearch(searchText);

          const rect = range.getBoundingClientRect();

          if (rect.top === 0 && rect.left === 0) {
            if (selection && selection.anchorNode?.parentElement) {
              const parentRect =
                selection.anchorNode.parentElement.getBoundingClientRect();
              setMentionPosition({
                top: parentRect.top,
                left: parentRect.left,
              });
            }
          } else {
            setMentionPosition({
              top: rect.top,
              left: rect.left,
            });
          }

          mentionStartPosRef.current = {
            node: node,
            offset: lastAtIndex,
          };

          const filtered = users.filter((user) =>
            user.name.toLowerCase().includes(searchText.toLowerCase())
          );
          setFilteredUsers(filtered);
          setSelectedMentionIndex(0);
          setShowMentions(true);
          return;
        }
      }
    }

    setShowMentions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showMentions && filteredUsers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        insertMention(filteredUsers[selectedMentionIndex]);
      } else if (e.key === "Escape") {
        setShowMentions(false);
      }
      return;
    }

    // Handle backspace for mention chips
    if (e.key === "Backspace") {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Check if cursor is at the start of a text node
      if (range.collapsed && range.startOffset === 0) {
        const node = range.startContainer;

        // Check if previous sibling is a mention chip
        let prevNode = node.previousSibling;

        // If current node is element, check its previous sibling
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          prevNode = element.previousElementSibling;
        }

        // Also check parent's previous sibling if we're at the start of a parent
        if (!prevNode && node.parentNode) {
          prevNode = node.parentNode.previousSibling;
        }

        if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
          const element = prevNode as HTMLElement;
          if (element.getAttribute("data-type") === "mention") {
            e.preventDefault();
            element.remove();
            updateContent();
            return;
          }
        }
      }

      // Check if we're right after a mention chip (with just a space)
      if (range.collapsed && range.startOffset === 1) {
        const node = range.startContainer;
        if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent?.charAt(0) === "\u00A0"
        ) {
          const prevNode = node.previousSibling;
          if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
            const element = prevNode as HTMLElement;
            if (element.getAttribute("data-type") === "mention") {
              e.preventDefault();
              element.remove();
              // Remove the space too
              if (node.textContent.length === 1) {
                node.parentNode?.removeChild(node);
              } else {
                node.textContent = node.textContent.substring(1);
              }
              updateContent();
              return;
            }
          }
        }
      }
    }

    // Handle delete key for mention chips
    if (e.key === "Delete") {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      if (range.collapsed) {
        const node = range.startContainer;

        // Check if next sibling is a mention chip
        let nextNode = node.nextSibling;

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          nextNode = element.nextElementSibling;
        }

        if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
          const element = nextNode as HTMLElement;
          if (element.getAttribute("data-type") === "mention") {
            e.preventDefault();
            element.remove();
            updateContent();
            return;
          }
        }
      }
    }
  };

  const toolbarButtons = [
    { icon: <Undo />, command: "undo", title: "Undo" },
    { icon: <Redo />, command: "redo", title: "Redo" },
    { icon: <Bold />, command: "bold", title: "Bold (Ctrl+B)" },
    { icon: <Italic />, command: "italic", title: "Italic (Ctrl+I)" },
    {
      icon: <Underline />,
      command: "underline",
      title: "Underline (Ctrl+U)",
    },
    {
      icon: <Strikethrough />,
      command: "strikeThrough",
      title: "Strikethrough",
    },
    {
      icon: <Heading1 />,
      command: "formatBlock",
      value: "h1",
      title: "Heading 1",
    },
    {
      icon: <Heading2 />,
      command: "formatBlock",
      value: "h2",
      title: "Heading 2",
    },
    {
      icon: <AlignLeft />,
      command: "justifyLeft",
      title: "Align Left",
    },
    {
      icon: <AlignCenter />,
      command: "justifyCenter",
      title: "Align Center",
    },
    {
      icon: <AlignRight />,
      command: "justifyRight",
      title: "Align Right",
    },
    {
      icon: <List />,
      command: "insertUnorderedList",
      title: "Bullet List",
    },
    {
      icon: <ListOrdered />,
      command: "insertOrderedList",
      title: "Numbered List",
    },
    {
      icon: <Quote />,
      command: "formatBlock",
      value: "blockquote",
      title: "Quote",
    },
    {
      icon: <Code />,
      command: "formatBlock",
      value: "pre",
      title: "Code Block",
    },
    {
      icon: <Link />,
      action: () => {
        saveSelection();
        setShowLinkModal(true);
      },
      title: "Insert Link",
    },
    {
      icon: <Link />,
      action: () => {
        saveSelection();
        setShowImageModal(true);
      },
      title: "Insert Image",
    },
    {
      icon: <Table />,
      action: () => setShowTableModal(true),
      title: "Insert Table",
    },
  ];

  const handleFocus = () => {
    const el = editorRef.current;
    if (!el) return;

    const cleanText = el.innerText.replace(/\u200B/g, "").trim();
    if (cleanText.length === 0) {
      el.dataset.placeholder = "";
    }
  };


  const handleBlur = () => {
    const el = editorRef.current;
    if (!el) return;

    const cleanText = el.innerText.replace(/\u200B/g, "").trim();
    if (cleanText.length === 0) {
      el.dataset.placeholder = "Start typing here... Type @ to insert fields";
    }
  };


  const handlePlaceholderInput = () => {
    const el = editorRef.current;
    if (!el) return;

    const cleanText = el.innerText.replace(/\u200B/g, "").trim();

    if (cleanText.length > 0) {
      el.dataset.placeholder = "";
    } else {
      el.dataset.placeholder = "Start typing here... Type @ to insert fields";
    }
  };


  return (
    <div className="bg-gray-100">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 p-3">
          <div className="flex flex-wrap gap-1">
            {toolbarButtons.map((btn, idx) => (
              <button
                key={idx}
                disabled={
                  plainTextMode &&
                  btn.command !== "undo" &&
                  btn.command !== "redo"
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (btn.action) {
                    btn.action();
                  } else {
                    execCommand(btn.command, btn.value);
                  }
                }}
                className={`p-2 rounded transition-colors border border-gray-300 bg-white hover:bg-gray-200 disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                title={btn.title}
                type="button"
              >
                {btn.icon}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div
            ref={editorRef}
            contentEditable
            onInput={(e) => {
              handlePlaceholderInput();
              handleInput();
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            suppressContentEditableWarning
            className="min-h-[190px] p-8 focus:outline-none editor relative"
            style={{
              lineHeight: "1.6",
              fontSize: "16px",
            }}
          >
          </div>
        </div>

        <div className="bg-gray-50 border-t border-gray-200 p-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="plainTextToggle"
            checked={plainTextMode}
            onChange={(e) => {
              const enabled = e.target.checked;
              setPlainTextMode(enabled);

              if (enabled) {
                // Store current HTML before converting to plain text
                if (editorRef.current) {
                  htmlContentBeforePlainText.current = editorRef.current.innerHTML;
                }
              } else {
                // Restore the original HTML content
                if (editorRef.current && htmlContentBeforePlainText.current) {
                  editorRef.current.innerHTML = htmlContentBeforePlainText.current;
                  htmlContentBeforePlainText.current = "";
                }
              }

              updateContent();
            }}
          />
          <label htmlFor="plainTextToggle" className="text-gray-800">
            Send as plain text (remove all HTML formatting)
          </label>
          {/* <details>
            <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
              View HTML Source
            </summary>
            <pre className="mt-3 p-4 bg-gray-800 text-green-400 rounded overflow-x-auto text-sm">
              <code>{content || "<p>Start typing to see HTML...</p>"}</code>
            </pre>
          </details> */}
        </div>
      </div>

      {/* Mention Dropdown */}
      {showMentions && filteredUsers.length > 0 && (
        <div
          className="fixed bg-white border border-gray-300 rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto"
          style={{
            top: `${mentionPosition.top}px`,
            left: `${mentionPosition.left}px`,
            minWidth: "300px",
          }}
        >
          {filteredUsers.map((user, idx) => (
            <div
              key={user.id}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(user);
              }}
              className={`px-4 py-2 cursor-pointer transition-colors ${idx === selectedMentionIndex
                ? "bg-blue-100 text-blue-900"
                : "hover:bg-gray-100"
                }`}
            >
              {/* <div className="font-medium">{user.name}</div> */}
              {user.name && type === "DYNAMIC" && (
                <div className=" font-medium">{user.name}</div>
              )}
              {user.name && type === "PREVIOUS_STAGE" && (
                <div className="font-medium">{`${user.name} > ${user.identifier}`}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Insert Table</h2>
              <button
                onClick={() => setShowTableModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rows
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tableRows}
                  onChange={(e) => setTableRows(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Columns
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tableCols}
                  onChange={(e) => setTableCols(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowTableModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={insertTable}
                  className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Insert Link</h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === "Enter" && insertLink()}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={insertLink}
                  className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Insert Image</h2>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === "Enter" && insertImage()}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowImageModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={insertImage}
                  className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomRichTextEditor;
